import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  output
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';

import { DecimalNumberDirective } from '@ph-shared/directives/decimal-numbers-only.directive';
import { NumberDirective } from '@ph-shared/directives/numbers-only.directive';
import { FORM_ERRORS } from '@ph-shared/utils/form-errors';

import { ControlErrorComponent } from '../control-error/control-error.component';
import { FormControlLabelComponent } from '../form-control-label/form-control-label.component';
import { Size } from '../form-input/form-input';
import { FormInputComponent } from '../form-input/form-input.component';

@Component({
  selector: 'ph-form-input-control',
  templateUrl: 'form-input-control.component.html',
  styleUrls: ['./form-input-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ControlErrorComponent,
    DecimalNumberDirective,
    FormControlLabelComponent,
    FormInputComponent,
    FormsModule,
    NumberDirective,
    ReactiveFormsModule,
  ],
  host: {
    '[class.ui-horizontal]': 'horizontal()',
    '(focusout)': 'onTouched()',
  },
})
export class FormInputControlComponent implements ControlValueAccessor {
  valueUpdated = output<string>();
  label = input('');
  placeholder = input('');
  onlyNumbers = input<boolean, boolean | string>(false, { transform: coerceBooleanProperty });
  decimalNumbersOnly = input<boolean, boolean | string>(false, { transform: coerceBooleanProperty });
  type = input('');
  required = input<boolean, boolean | string>(false, { transform: coerceBooleanProperty });
  readOnly = input<boolean, boolean | string>(false, { transform: coerceBooleanProperty });
  inputSize = input<Size>('large' as Size);
  maxLength = input<number | undefined>();
  inactive = input<boolean, boolean | string>(false, { transform: coerceBooleanProperty });
  horizontal = input<boolean, boolean | string>(false, { transform: coerceBooleanProperty });
  disabledAutocomplete = input<boolean, boolean | string>(false, { transform: coerceBooleanProperty });

  value: string = '';
  disabled: boolean = false;
  errorMessage: string = '';
  id = computed(() => (this.label() ? this.label().replace(/\s/g, '') : 'undefined_id'));

  private cdr = inject(ChangeDetectorRef);
  private _errors = inject(FORM_ERRORS);
  private control = inject(NgControl, { self: true, optional: true });

  constructor() {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  onTouched() {
    this._onTouched();
  }

  get showError(): boolean {
    if (!this.control) {
      return false;
    }

    const { dirty, touched, errors } = this.control;

    if (errors) {
      if (errors.errorMessage) {
        this.errorMessage = errors.errorMessage;
      } else {
        const firstKey = Object.keys(errors)[0];
        const getError = this._errors[firstKey];
        this.errorMessage = getError ? getError(errors[firstKey]) : '';
      }
    }

    return this.invalid ? !!(dirty || touched) : false;
  }

  get invalid(): boolean {
    return this.control ? !!this.control.invalid : false;
  }

  writeValue(value: string): void {
    this.value = value || '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (val: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  updateValue(val: string): void {
    this.writeValue(val);
    this.valueUpdated.emit(val);
    this._onChange(this.value);
    this.cdr.markForCheck();
    this._onTouched();
  }

  private _onChange: (value: string) => void = () => undefined;
  private _onTouched: () => void = () => undefined;
}
